import { useEffect, useRef } from 'react';
import { toast } from '@/lib/toast';

/**
 * Polls the deployed build manifest (`/version.json`) and, when it no longer
 * matches the commit this bundle was built from, surfaces a persistent,
 * non-dismissible toast asking the user to refresh.
 *
 * The manifest is emitted at build time by the `version-manifest` Vite plugin
 * (see vite.config.ts) and served with no caching, so each poll reflects what
 * Cloudflare Pages currently has deployed. This lets a user who has the app
 * open in a stale tab pick up a new deploy without us shipping a stale build.
 */

const POLL_INTERVAL_MS = 2 * 60 * 1000; // every 2 minutes
const VERSION_MANIFEST_URL = '/version.json';

function showUpdateToast(): void {
  toast('A new version is available', {
    description:
      'You’re viewing an outdated version of the app. Refresh to load the latest updates.',
    duration: Infinity,
    dismissible: false,
    action: {
      label: 'Refresh now',
      onClick: () => window.location.reload(),
    },
  });
}

/**
 * Fetches the currently deployed commit hash, or `null` if it can't be
 * determined (offline, transient error, or an SPA fallback served HTML).
 * Returning `null` is always safe — the next poll simply tries again.
 */
async function fetchDeployedCommit(): Promise<string | null> {
  try {
    // The query param defeats any CDN/proxy caching; `no-store` defeats the
    // browser cache. Together they guarantee a fresh read of the manifest.
    const res = await fetch(`${VERSION_MANIFEST_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;

    // An SPA fallback can answer an unknown path with index.html — only trust
    // a genuine JSON response.
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) return null;

    const data: unknown = await res.json();
    const commit = (data as { commit?: unknown }).commit;
    return typeof commit === 'string' && commit.length > 0 ? commit : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(): void {
  const notifiedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | undefined;

    const stopPolling = (): void => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const check = async (): Promise<void> => {
      if (cancelled || notifiedRef.current) return;

      const deployedCommit = await fetchDeployedCommit();
      if (cancelled || notifiedRef.current || deployedCommit === null) return;
      if (deployedCommit === __COMMIT_HASH__) return;

      // A new build is live. Notify once, then stop polling — the toast is
      // persistent, so there is nothing left to watch for.
      notifiedRef.current = true;
      stopPolling();
      showUpdateToast();
    };

    void check();
    intervalId = window.setInterval(() => void check(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, []);
}
